import React, { useState, useEffect } from "react";
import NosotrosIMG from "./assets/escuelaBiblica3.jpg";
import { Typography } from "@mui/material";
import "./recursos.css";
import OracionIMG from "./thumbnails/oracion.jpg";
import SeminarioIMG from "./thumbnails/seminario.jpg";
import PredicaIMG from "./thumbnails/predica.jpg";
import EstudioIMG from "./thumbnails/young_women_scriptures_stuyding_pencils.webp";
import EspiritualIMG from "./thumbnails/patrick-fore-b_SHPU5M3nk-unsplash.jpg";
import BibliaIMG from "./thumbnails/aaron-burden-4uX_r8OhJ_o-unsplash.jpg";
import NuevaVidaIMG from "./thumbnails/samuel-martins-3U7HcqkIbb4-unsplash.jpg";
import PanoramaIMG from "./thumbnails/jametlene-reskp-Cm8KUxo4ggw-unsplash.jpg";
import SincerelyIMG from "./thumbnails/sincerely-media-dGxOgeXAXm8-unsplash (1).jpg";

const API_URL = "https://iglesiaevangelicadelafe.com/api-church/lessons.php";
const BASE_URL = "https://iglesiaevangelicadelafe.com/api-church/";

// Dynamically relate thumbnails to the lesson content
const getLessonImage = (lesson) => {
  const text = `${lesson.title} ${lesson.description} ${lesson.category}`.toLowerCase();
  
  if (text.includes("oración") || text.includes("oracion") || text.includes("orar")) return OracionIMG;
  if (text.includes("estudio") || text.includes("jovenes") || text.includes("mujeres")) return EstudioIMG;
  if (text.includes("seminario") || text.includes("taller") || text.includes("curso")) return SeminarioIMG;
  if (text.includes("predica") || text.includes("sermon") || text.includes("mensaje") || text.includes("pastor")) return PredicaIMG;
  if (text.includes("nueva vida") || text.includes("bautismo") || text.includes("salvacion") || text.includes("evangelio")) return NuevaVidaIMG;
  if (text.includes("panorama") || text.includes("historia") || text.includes("testamento")) return PanoramaIMG;
  if (text.includes("crecimiento") || text.includes("madurez") || text.includes("fe")) return EspiritualIMG;
  if (text.includes("biblia") || text.includes("escritura") || text.includes("palabra")) return BibliaIMG;
  
  // Standard fallbacks if no keyword is matched
  if (lesson.category === "Crecimiento Espiritual") return EspiritualIMG;
  if (lesson.category === "Panorama biblico") return PanoramaIMG;
  if (lesson.category === "Nueva vida") return NuevaVidaIMG;
  if (lesson.category === "Seminarios") return SeminarioIMG;
  if (lesson.category === "Predicas") return PredicaIMG;

  return SincerelyIMG; // Best generic fallback
};

const CATEGORY_LIST = [
  "Todas",
  "Crecimiento Espiritual",
  "Panorama biblico",
  "Nueva vida",
  "Seminarios",
  "Predicas"
];

function Recursos() {
  const [lessons, setLessons] = useState([]);
  const [filter, setFilter] = useState("Todas");
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await fetch(API_URL);
      const text = await response.text();

      try {
        const data = JSON.parse(text);
        if (data.success) {
          setLessons(data.data);
        } else {
          setErrorCode(true);
        }
      } catch (parseError) {
        console.error("No JSON received. Response:", text.substring(0, 50));
        setErrorCode(true);
      }
    } catch (error) {
      console.error("Request failed:", error);
      setErrorCode(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
    // Scroll directly to the content section below the header so it's perfectly in view
    setTimeout(() => {
      document.getElementById('recursos-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const filteredLessons = filter === "Todas" 
    ? lessons 
    : lessons.filter(l => l.category === filter);

  return (
    <>
      {/* Hero image (Always visible so Navbar text stays readable over the dark image) */}
      <div className="img-container">
        <img src={NosotrosIMG} alt="recursos-iglesia-la-fe" />
        <Typography variant="h1" className="img-title">
          Recursos
        </Typography>
      </div>

      {/* Main Content */}
      <section className="recursos-section" id="recursos-content">
        <div className="container">
          {!selectedLesson ? (
            <div className="content-wrapper" style={{ paddingTop: '60px', paddingBottom: '60px' }}>
              <div className="recursos-header">
                <h2>Clases y Material Bíblico</h2>
                <p>
                  Explora y descarga material valioso para tu crecimiento espiritual.
                  Selecciona una categoría para filtrar nuestro contenido.
                </p>
              </div>

              <div className="recursos-filters">
                {CATEGORY_LIST.map(cat => (
                  <button 
                    key={cat}
                    className={`filter-btn ${filter === cat ? 'active' : ''}`}
                    onClick={() => setFilter(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="recursos-grid">
                {loading && (
                  <div className="recursos-loading">
                    <Typography variant="h5">Cargando recursos...</Typography>
                  </div>
                )}

                {!loading && errorCode && (
                  <div className="recursos-error">
                    <h3>Oops! No pudimos conectar.</h3>
                    <p>Por favor revisa más tarde cuando nuestro servidor esté en línea.</p>
                  </div>
                )}

                {!loading && !errorCode && filteredLessons.length === 0 && (
                  <div className="recursos-empty">
                    <Typography variant="h5">Próximamente</Typography>
                    <p>Aún no hay materiales en {filter === "Todas" ? "nuestra base de datos" : `la categoría "${filter}"`}.</p>
                  </div>
                )}

                {!loading && !errorCode && filteredLessons.map((lesson) => {
                  const imageUrl = getLessonImage(lesson);

                  return (
                    <div 
                      key={lesson.id} 
                      className="recurso-card clickable-card"
                      onClick={() => handleLessonClick(lesson)}
                    >
                      <div className="recurso-image-container">
                        <img src={imageUrl} alt={lesson.title} className="recurso-image" />
                      </div>
                      <div className="recurso-content">
                        <div className="recurso-meta">
                          <span className="recurso-category">{lesson.category}</span>
                          <span className="recurso-date">
                            {lesson.created_at 
                              ? new Date(lesson.created_at).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })
                              : "Reciente"}
                          </span>
                        </div>
                        <h3 className="recurso-title">{lesson.title}</h3>
                        <p className="recurso-desc">
                          {lesson.description || "Sin descripción proporcionada."}
                        </p>
                        
                        <div className="recurso-btn-container">
                          <span className="recurso-btn">
                            Leer y Descargar
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* PDF VIEWER MODE */
            <div className="pdf-viewer-wrapper" style={{ paddingTop: '80px', paddingBottom: '60px' }}>
              <div className="pdf-viewer-header">
                <div>
                  <button onClick={() => setSelectedLesson(null)} className="back-btn">
                    &larr; Volver a Recursos
                  </button>
                </div>
                <div className="pdf-title-container">
                  <h2 className="pdf-title">{selectedLesson.title}</h2>
                  <span className="recurso-category" style={{ marginBottom: 0 }}>{selectedLesson.category}</span>
                </div>
                <div>
                  <a 
                    href={`${BASE_URL}${selectedLesson.file_url}`} 
                    target="_blank" 
                    rel="noreferrer"
                    className="download-btn"
                    download={`${selectedLesson.title || 'leccion'}.pdf`}
                  >
                    Descargar PDF
                  </a>
                </div>
              </div>
              
              <div className="pdf-embed-container">
                <iframe 
                  src={`${BASE_URL}${selectedLesson.file_url}`} 
                  title={selectedLesson.title}
                  className="pdf-iframe"
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Recursos;
